import { useNavigate, useParams } from "@tanstack/solid-router";
import { createEffect, createSignal, For, onCleanup, onMount, Show } from "solid-js";
import { fetchMe } from "~/lib/api";
import {
  applyLive,
  displayBody,
  fetchChatChannel,
  fetchChatHistory,
  markChatRead,
  postChatMessage,
  redactChatMessage,
  setChatMuted,
  streamChat,
  TOMBSTONE,
  type ChatMessage,
} from "~/lib/chat";

export function ChatRoomPage() {
  const params = useParams({ strict: false });
  const navigate = useNavigate();
  const channelId = () => params().channelId ?? "";
  const [title, setTitle] = createSignal("Room");
  const [role, setRole] = createSignal("attendee");
  const [muted, setMuted] = createSignal(false);
  const [pinned, setPinned] = createSignal<ChatMessage | null>(null);
  const [toast, setToast] = createSignal("");
  const [messages, setMessages] = createSignal<ChatMessage[]>([]);
  const [draft, setDraft] = createSignal("");
  const [error, setError] = createSignal("");
  const [me, setMe] = createSignal("");
  let list: HTMLDivElement | undefined;

  function scrollDown() {
    queueMicrotask(() => {
      if (list) list.scrollTop = list.scrollHeight;
    });
  }

  function note(message: ChatMessage) {
    if (message.kind === "announcement" && message.body) {
      setPinned(message);
      setToast(message.body);
    }
    if (message.seq) void markChatRead(channelId(), message.seq).catch(() => {});
  }

  onMount(() => {
    const controller = new AbortController();
    void (async () => {
      try {
        const who = await fetchMe();
        setMe(who.data.zitadel_id);
        const channel = await fetchChatChannel(channelId());
        setTitle(channel.title || channelId());
        setRole(channel.role);
        setMuted(Boolean(channel.muted));
        setPinned(channel.pinned ?? null);
        const history = await fetchChatHistory(channelId());
        setMessages(history.messages ?? []);
        scrollDown();
        const latest = history.messages?.at(-1)?.seq ?? history.latest_seq ?? 0;
        if (latest) void markChatRead(channelId(), latest).catch(() => {});
        await streamChat(channelId(), latest, controller.signal, (event) => {
          if (event.event === "gap") {
            void fetchChatHistory(channelId()).then((next) => {
              setMessages(next.messages ?? []);
              scrollDown();
            });
            return;
          }
          if (event.event !== "message") return;
          setMessages((current) => applyLive(current, event.message));
          note(event.message);
          scrollDown();
        });
      } catch (err) {
        if (!controller.signal.aborted) setError((err as Error).message);
      }
    })();
    onCleanup(() => controller.abort());
  });

  createEffect(() => {
    if (!toast()) return;
    const timer = window.setTimeout(() => setToast(""), 4000);
    onCleanup(() => window.clearTimeout(timer));
  });

  async function send(kind = "chat") {
    const body = draft().trim();
    if (!body) return;
    setDraft("");
    setError("");
    try {
      const sent = await postChatMessage(channelId(), body, kind);
      setMessages((current) => applyLive(current, { ...sent, body, kind, user_id: me() }));
      note({ ...sent, body, kind });
      scrollDown();
    } catch (err) {
      setDraft(body);
      setError((err as Error).message);
    }
  }

  const canModerate = () => role() === "organiser" || role() === "admin";

  return (
    <section class="chat-room">
      <header class="chat-room-bar">
        <button type="button" class="btn btn-ghost" onClick={() => navigate({ to: "/meetup" })}>
          Back
        </button>
        <strong>{title()}</strong>
        <button
          type="button"
          class="btn btn-ghost"
          onClick={() => {
            const next = !muted();
            setMuted(next);
            void setChatMuted(channelId(), next).catch((err) => setError((err as Error).message));
          }}
        >
          {muted() ? "Unmute" : "Mute"}
        </button>
      </header>
      <Show when={pinned()?.body}>
        <p class="chat-pin">{pinned()!.body}</p>
      </Show>
      <Show when={toast()}>
        <p class="chat-toast" role="status">
          {toast()}
        </p>
      </Show>
      <div class="chat-transcript" ref={list}>
        <Show when={messages().length === 0 && !error()}>
          <p class="chat-empty">The room is open.</p>
        </Show>
        <For each={messages()}>
          {(message) => {
            const mine = () => message.user_id === me();
            const tombstone = () => displayBody(message) === TOMBSTONE;
            return (
              <article
                class={`chat-bubble${mine() ? " chat-bubble--mine" : ""}${tombstone() ? " chat-bubble--tombstone" : ""}`}
                onContextMenu={(event) => {
                  if (!canModerate() || tombstone()) return;
                  event.preventDefault();
                  void redactChatMessage(channelId(), message.seq).catch((err) => setError((err as Error).message));
                }}
              >
                <Show when={!mine()}>
                  <span>{message.user}</span>
                </Show>
                <p>{displayBody(message)}</p>
              </article>
            );
          }}
        </For>
      </div>
      <Show when={error()}>
        <p class="error-box">{error()}</p>
      </Show>
      <form
        class="chat-composer"
        onSubmit={(event) => {
          event.preventDefault();
          void send();
        }}
      >
        <input
          value={draft()}
          placeholder="Message the room"
          aria-label="Message"
          onInput={(event) => setDraft(event.currentTarget.value)}
        />
        <button type="submit" class="btn btn-primary" disabled={!draft().trim()}>
          Send
        </button>
      </form>
    </section>
  );
}
